import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import FloorPlanSlider from "./FloorPlanSlider";

const ModernElegance = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const fadeInVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
  };

  const staggerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-[#f8f9fa] to-[#e9ecef]"
      id="rivera-living"
    >
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={`particle-${i}`}
            className="absolute h-2 w-2 rounded-full bg-gradient-to-r from-gold-300 to-gold-500"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.3,
            }}
            animate={{
              y: [0, Math.random() * 100 - 50],
              x: [0, Math.random() * 100 - 50],
              scale: [1, 1.5, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Animated gradient orbs */}
      <motion.div
        className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-gradient-to-br from-gold-300/20 to-gold-500/20 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse",
        }}
      />
      <motion.div
        className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-gradient-to-tr from-gold-500/20 to-gold-300/20 blur-3xl"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.4, 0.6, 0.4],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          repeatType: "reverse",
          delay: 1,
        }}
      />

      <div className="container relative mx-auto px-4 max-w-7xl">
        {/* Elegant Header */}
        <motion.div
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          variants={fadeInVariants}
          className="mb-16 text-center"
        >
          <motion.div
            className="mb-6 inline-flex items-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="h-[1px] w-12 bg-gradient-to-r from-transparent to-gold-400" />
            <span className="mx-4 text-sm font-semibold uppercase tracking-widest text-gold-500">
            Grow and Thrive
            </span>
            <div className="h-[1px] w-12 bg-gradient-to-l from-transparent to-gold-400" />
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-midnightBlack mb-6">
          Green Living,{" "}
            <span className="bg-gradient-to-r from-gold-400 to-gold-600 bg-clip-text text-transparent">
            Redefined
            </span>
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-gold-400 to-gold-600 mx-auto"></div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content Section */}
          <motion.div
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
            variants={staggerVariants}
            className="space-y-6"
          >
            <motion.p
              variants={fadeInVariants}
              className="text-lg text-silver-600 leading-relaxed"
            >
              Rivera at The Valley by Emaar offers premium villas in Dubai's tranquil suburban community. The development features lush green spaces, family amenities, and exceptional architecture. These villas combine sophisticated design with natural surroundings, providing functional and aesthetic living spaces. The project appeals to investors and homebuyers seeking quality real estate with long-term value in Dubai's expanding market.
            </motion.p>

            <motion.ul variants={staggerVariants} className="space-y-4">
              {[
                {
                  title: "Designed to Breathe",
                  description:
                    "Wake up to landscaped views and unwind in nature-inspired outdoor spaces.",
                },
                {
                  title: "Connected Yet Secluded",
                  description: "Enjoy peaceful living with easy access to city conveniences and key destinations.",
                },
                {
                  title: "Walk the Green Way",
                  description:
                    "Enjoy serene trails, landscaped paths, and scenic routes woven into the community",
                },
              ].map((feature, index) => (
                <motion.li
                  key={index}
                  variants={fadeInVariants}
                  className="group flex items-start p-4 rounded-xl transition-all duration-300 hover:bg-white/50 hover:shadow-lg hover:shadow-gold-300/10"
                >
                  <div className="bg-gradient-to-br from-gold-300 to-gold-500 rounded-full p-1 mr-3 mt-1.5 group-hover:scale-110 transition-transform duration-300">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-white"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <span className="font-semibold text-midnightBlack group-hover:text-gold-500 transition-colors duration-300">
                      {feature.title}
                    </span>
                    <p className="text-silver-600">{feature.description}</p>
                  </div>
                </motion.li>
              ))}
            </motion.ul>

            <motion.div variants={fadeInVariants} className="mt-8">
              <p className="text-silver-600 mb-2">
                Community amenities include:
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  "River Meandering",
                  "Swimming Pool",
                  "Restaurents",
                  "Cycling Tracks",
                  "Dog Parks",,
                  "Fitness Centre",
                  "Kids Play Area",
                  "Flower Farm"
                ].map((option, index) => (
                  <motion.span
                    key={index}
                    className="px-4 py-1.5 bg-white/80 rounded-full text-sm font-medium text-gold-500 shadow-sm hover:shadow-md hover:bg-white hover:scale-105 transition-all duration-300 cursor-pointer"
                    whileHover={{ y: -2 }}
                  >
                    {option}
                  </motion.span>
                ))}
              </div>
            </motion.div>

            <motion.div
              variants={fadeInVariants}
              className="mt-8 p-6 border border-gold-300 rounded-xl bg-gradient-to-r from-gold-100/20 to-transparent backdrop-blur-sm hover:shadow-xl hover:shadow-gold-300/10 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
            >
              <p className="text-xl font-semibold text-deepWine">
              Starting Prize Upon Request
              </p>
              <p className="text-silver-600">
                for sustainable living space
              </p>
            </motion.div>
          </motion.div>

          {/* Visual Section */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="space-y-8"
          >
            {/* Floor Plans Slider */}
            <motion.div
              className="rounded-xl overflow-hidden shadow-xl hover:shadow-2xl hover:shadow-gold-300/20 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
            >
              <FloorPlanSlider />
            </motion.div>

            {/* Lifestyle Image */}
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={isVisible ? { y: 0, opacity: 1 } : { y: 40, opacity: 0 }}
              transition={{ duration: 0.9, delay: 0.6 }}
              className="relative h-64 md:h-80 rounded-xl overflow-hidden shadow-lg hover:shadow-xl hover:shadow-gold-300/20 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
            >
              <div className="absolute inset-0 overflow-hidden">
                <div className="w-full h-full transform scale-110 animate-float">
                  <img
                    src="/Images/RIVERA_THE_VALLEY_IMAGE06.jpg"
                    alt="Nature-inspired community with green spaces"
                    className="object-cover w-full h-full"
                  />
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-midnightBlack/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-white font-medium">
                  Nature-themed haven with emerald forests and flower gardens
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ModernElegance;
